// BlurredPreview.js — Phase 2 (Feb 2026)
// Modal shown to free-tier users after their 1 free generation completes.
// Renders EN + AR blurred, watermarked preview images side-by-side. The
// server pre-applies Gaussian blur and a "PREVIEW — PAY TO UNLOCK" band,
// so CSS-only un-blur devtools tricks cannot reveal the underlying text.
// We layer another CSS blur + watermark on top for good measure and to
// discourage screenshotting.
//
// The two panes are fetched INDEPENDENTLY so a slow AR auto-translation
// (which runs after the EN version completes) does not kill the whole
// modal. Each pane manages its own loading / error / retry lifecycle.
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { X, Lock, Lightning, ArrowClockwise } from "@phosphor-icons/react";
import { useLang } from "@/context/LangContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fetch a preview PNG as a blob URL — required because <img src> tags
// cannot carry an Authorization header, and we want the JWT to stay in
// localStorage (not the URL) whenever possible.
async function fetchPreviewBlob(reportId, lang, page) {
  const token = localStorage.getItem("esg_token") || "";
  const res = await fetch(
    `${API}/reports/${reportId}/preview-image?lang=${lang}&page=${page}`,
    { headers: token ? { Authorization: `Bearer ${token}` } : {}, credentials: "include" }
  );
  if (!res.ok) {
    let detail = {};
    try { detail = await res.json(); } catch { /* ignore */ }
    const err = new Error(detail?.detail?.message || `Preview failed (${res.status})`);
    err.status = res.status;
    err.code = detail?.detail?.error;
    throw err;
  }
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

// Independent state machine for each language pane so a slow AR
// translation cannot break the EN pane and vice versa.
function usePaneLoader(reportId, lang, previewPage, isOpen) {
  const [url, setUrl] = useState(null);
  const [status, setStatus] = useState("idle"); // idle | loading | ready | pending | error
  const [message, setMessage] = useState(null);

  const load = useCallback(() => {
    if (!reportId) return;
    setStatus("loading");
    setMessage(null);
    fetchPreviewBlob(reportId, lang, previewPage)
      .then((u) => {
        setUrl(u);
        setStatus("ready");
      })
      .catch((e) => {
        if (e.status === 425 || e.code === "translation_pending") {
          setStatus("pending");
        } else {
          setStatus("error");
        }
        setMessage(e.message || null);
      });
  }, [reportId, lang, previewPage]);

  useEffect(() => {
    if (!isOpen) return;
    load();
    return () => { if (url) URL.revokeObjectURL(url); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, reportId, lang, previewPage]);

  // Auto-retry pending panes every 30s while the modal is open —
  // matches the ~3-5 min AR translation window.
  useEffect(() => {
    if (!isOpen || status !== "pending") return;
    const t = setTimeout(load, 30000);
    return () => clearTimeout(t);
  }, [isOpen, status, load]);

  return { url, status, message, reload: load };
}

export default function BlurredPreview({ reportId, isOpen, onClose, expiresAt, previewPage = 5 }) {
  const { lang } = useLang();
  const navigate = useNavigate();
  const en = usePaneLoader(reportId, "en", previewPage, isOpen);
  const ar = usePaneLoader(reportId, "ar", previewPage, isOpen);

  // Body scroll lock while modal is open
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [isOpen]);

  if (!isOpen) return null;

  const expiryText = (() => {
    if (!expiresAt) return null;
    try {
      const exp = new Date(expiresAt);
      const now = new Date();
      const hoursLeft = Math.max(0, Math.round((exp - now) / 36e5));
      if (lang === "ar") return `المعاينة تنتهي خلال ~${hoursLeft} ساعة`;
      return `Preview expires in ~${hoursLeft}h`;
    } catch { return null; }
  })();

  const blockCopy = (e) => { e.preventDefault(); };

  return (
    <div
      data-testid="blurred-preview-modal"
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/85 backdrop-blur-sm px-3 py-6 sm:p-6"
      onContextMenu={blockCopy}
    >
      <div className="relative w-full max-w-6xl max-h-[92vh] overflow-y-auto bg-[#0B0C10] border border-white/10 rounded-2xl shadow-2xl">
        <button
          data-testid="blurred-preview-close"
          onClick={onClose}
          className="absolute top-3 right-3 z-10 p-2 rounded-full bg-white/5 hover:bg-white/15 text-slate-300 hover:text-white transition-colors"
          aria-label="Close preview"
        >
          <X size={20} weight="bold" />
        </button>

        <div className="p-5 sm:p-8">
          <div className="mb-5 sm:mb-6">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#F97316]/15 border border-[#F97316]/40 text-[#F97316] text-xs font-['Outfit'] font-bold tracking-wide uppercase">
              <Lock size={13} weight="fill" /> {lang === "ar" ? "معاينة مجانية مموّهة" : "Free blurred preview"}
            </div>
            <h2 className="mt-3 font-['Outfit'] text-2xl sm:text-3xl font-bold text-white">
              {lang === "ar" ? "تقريرك جاهز — قم بالترقية لفتحه" : "Your report is ready — upgrade to unlock"}
            </h2>
            <p className="mt-2 text-sm text-slate-400 leading-relaxed max-w-3xl">
              {lang === "ar"
                ? "هذه صورة مموّهة لصفحة واحدة من تقريرك ثنائي اللغة (60+ صفحة). اشترك بـ 499 $ / سنة لفتح التنزيل الكامل باللغتين الإنجليزية والعربية، مع التعديلات والتحديثات غير المحدودة."
                : "This is a blurred snapshot of a single page from your full 60+ page bilingual ESG report. Subscribe for $499/year to unlock the complete English & Arabic download, with unlimited amendments."}
            </p>
            {expiryText && (
              <p className="mt-2 text-xs text-[#F97316] font-semibold">{expiryText}</p>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
            <PreviewPane
              testId="preview-pane-en"
              state={en}
              caption={lang === "ar" ? "النسخة الإنجليزية" : "English edition"}
              accent="#01AF9B"
              uiLang={lang}
            />
            <PreviewPane
              testId="preview-pane-ar"
              state={ar}
              caption={lang === "ar" ? "النسخة العربية" : "Arabic edition (عربي)"}
              accent="#B94B05"
              uiLang={lang}
            />
          </div>

          <div className="mt-7 sm:mt-8 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4 border-t border-white/10 pt-6">
            <div className="text-sm text-slate-400 leading-relaxed max-w-xl">
              {lang === "ar"
                ? "بمجرد الاشتراك، تحصل على تقارير ESG غير محدودة، وتعديلات غير محدودة طوال العام."
                : "One subscription unlocks unlimited ESG reports and unlimited amendments for a full year."}
            </div>
            <button
              data-testid="blurred-preview-subscribe"
              onClick={() => navigate("/billing")}
              className="inline-flex items-center gap-2 bg-gradient-to-r from-[#01AF9B] to-[#018F7E] hover:opacity-90 text-white font-['Outfit'] font-bold text-base py-3.5 px-6 rounded-xl transition-all whitespace-nowrap"
            >
              <Lightning size={18} weight="fill" />
              {lang === "ar" ? "اشترك بـ 499 $ / سنة" : "Subscribe $499/year"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function PreviewPane({ state, caption, accent, testId, uiLang }) {
  const blockCopy = (e) => { e.preventDefault(); };
  const { url, status, message, reload } = state;

  return (
    <div className="relative rounded-xl overflow-hidden border border-white/10 bg-white/[0.02]" data-testid={testId}>
      <div
        className="relative w-full select-none bg-black"
        onContextMenu={blockCopy}
        onDragStart={blockCopy}
        style={{ userSelect: "none", minHeight: status === "ready" ? "auto" : "560px" }}
      >
        {status === "ready" && url && (
          <>
            <img
              src={url}
              alt=""
              draggable={false}
              className="w-full h-auto pointer-events-none block"
            />
            <div
              className="absolute inset-0 pointer-events-none flex items-center justify-center"
              style={{
                backgroundImage:
                  "repeating-linear-gradient(135deg, rgba(255,255,255,0.03) 0 40px, rgba(0,0,0,0.06) 40px 80px)",
              }}
            >
              <span
                className="font-['Outfit'] font-black tracking-[0.25em] text-white/20 text-2xl sm:text-3xl uppercase whitespace-nowrap"
                style={{ transform: "rotate(-22deg)" }}
              >
                Preview · Pay to Unlock
              </span>
            </div>
          </>
        )}

        {status === "loading" && (
          <div className="w-full h-full flex flex-col items-center justify-center gap-3 text-slate-500 text-xs">
            <div className="w-8 h-8 border-2 border-[#01AF9B] border-t-transparent rounded-full animate-spin" />
            <span>{uiLang === "ar" ? "جاري التحميل…" : "Loading…"}</span>
          </div>
        )}

        {status === "pending" && (
          <div className="w-full h-full flex flex-col items-center justify-center gap-3 px-6 text-center">
            <div className="w-10 h-10 border-2 border-[#F97316] border-t-transparent rounded-full animate-spin" />
            <div className="text-[#F97316] text-xs font-['Outfit'] font-bold tracking-wide uppercase">
              {uiLang === "ar" ? "الترجمة قيد التنفيذ" : "Translation in progress"}
            </div>
            <p className="text-slate-400 text-xs leading-relaxed max-w-[280px]">
              {uiLang === "ar"
                ? "الترجمة العربية تكتمل عادةً خلال 3–5 دقائق بعد النسخة الإنجليزية. سيتم تحديث المعاينة تلقائيًا."
                : "The Arabic translation typically finishes within 3–5 minutes after the English version. This pane will refresh automatically."}
            </p>
            <button
              onClick={reload}
              className="inline-flex items-center gap-1.5 text-[10px] font-['Outfit'] font-bold tracking-wider uppercase text-slate-400 hover:text-white border border-white/10 hover:border-white/30 rounded px-3 py-1.5 transition-colors"
            >
              <ArrowClockwise size={12} weight="bold" /> {uiLang === "ar" ? "تحديث الآن" : "Refresh now"}
            </button>
          </div>
        )}

        {status === "error" && (
          <div className="w-full h-full flex flex-col items-center justify-center gap-3 px-6 text-center">
            <div className="text-[#EF4444] text-2xl">✕</div>
            <p className="text-slate-300 text-xs leading-relaxed max-w-[280px]">
              {message || (uiLang === "ar" ? "تعذّر تحميل المعاينة." : "Could not load preview.")}
            </p>
            <button
              onClick={reload}
              className="inline-flex items-center gap-1.5 text-[10px] font-['Outfit'] font-bold tracking-wider uppercase text-slate-400 hover:text-white border border-white/10 hover:border-white/30 rounded px-3 py-1.5 transition-colors"
            >
              <ArrowClockwise size={12} weight="bold" /> {uiLang === "ar" ? "إعادة المحاولة" : "Retry"}
            </button>
          </div>
        )}
      </div>
      <div className="px-3 py-2 flex items-center gap-2 border-t border-white/5">
        <span className="w-1.5 h-1.5 rounded-full" style={{ background: accent }} />
        <span className="text-xs text-slate-400 font-['Outfit'] font-semibold">{caption}</span>
      </div>
    </div>
  );
}
